/**
 * DungeonIO.ts
 * Export and import .bbd dungeon files (JSON format).
 * Uses existing serialization helpers from OctileGrid.ts.
 */

import type { OctileDungeon } from '../types/OctileTypes.ts'
import { serializeDungeon, deserializeGrid } from '../grid/OctileGrid.ts'

// ─── Export ──────────────────────────────────────────────────

/**
 * Export a dungeon as a downloadable .bbd file.
 */
export function exportBBD(dungeon: OctileDungeon): void {
    dungeon.modifiedAt = new Date().toISOString()
    const json = serializeDungeon(dungeon)
    const blob = new Blob([json], { type: 'application/json' })
    const url = URL.createObjectURL(blob)

    const filename = sanitizeFilename(dungeon.name) + '.bbd'
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
}

/**
 * Serialize a dungeon to a JSON string (for testing without triggering download).
 */
export function serializeBBD(dungeon: OctileDungeon): string {
    dungeon.modifiedAt = new Date().toISOString()
    return serializeDungeon(dungeon)
}

// ─── Import ──────────────────────────────────────────────────

/**
 * Import a dungeon from a .bbd file.
 * Reads the file, validates structure, and reconstructs the full grid.
 */
export async function importBBD(file: File): Promise<OctileDungeon> {
    const text = await readFileAsText(file)

    let data: unknown
    try {
        data = JSON.parse(text)
    } catch {
        throw new Error('Invalid JSON in .bbd file')
    }

    const validation = validateBBD(data)
    if (!validation.valid) {
        throw new Error(`Invalid .bbd file: ${validation.errors.join(', ')}`)
    }

    return reconstructDungeon(data as Record<string, unknown>)
}

/**
 * Parse and reconstruct a dungeon from a raw JSON object (for testing).
 */
export function importBBDFromJSON(json: string): OctileDungeon {
    const data = JSON.parse(json)
    const validation = validateBBD(data)
    if (!validation.valid) {
        throw new Error(`Invalid .bbd data: ${validation.errors.join(', ')}`)
    }
    return reconstructDungeon(data)
}

// ─── Validation ──────────────────────────────────────────────

/**
 * Validate that a parsed object has the required BBD structure.
 */
export function validateBBD(data: unknown): { valid: boolean; errors: string[] } {
    const errors: string[] = []

    if (!data || typeof data !== 'object') {
        return { valid: false, errors: ['Data is not an object'] }
    }

    const d = data as Record<string, unknown>

    if (typeof d.id !== 'string') errors.push('Missing or invalid "id"')
    if (typeof d.name !== 'string') errors.push('Missing or invalid "name"')
    if (typeof d.seed !== 'number') errors.push('Missing or invalid "seed"')
    if (typeof d.version !== 'string') errors.push('Missing or invalid "version"')
    if (typeof d.octagonCols !== 'number') errors.push('Missing "octagonCols"')
    if (typeof d.octagonRows !== 'number') errors.push('Missing "octagonRows"')

    if (!Array.isArray(d.layers) || d.layers.length === 0) {
        errors.push('Missing or empty "layers" array')
    } else {
        for (let i = 0; i < d.layers.length; i++) {
            const layer = d.layers[i] as Record<string, unknown>
            if (!Array.isArray(layer.grid)) {
                errors.push(`Layer ${i}: missing "grid" array`)
            }
        }
    }

    if (!d.entrance || typeof d.entrance !== 'object') {
        errors.push('Missing "entrance"')
    }

    if (!d.dna || typeof d.dna !== 'object') {
        errors.push('Missing "dna"')
    } else {
        const dna = d.dna as Record<string, unknown>
        if (typeof dna.personality !== 'string') {
            errors.push('Missing "dna.personality"')
        }
    }

    return { valid: errors.length === 0, errors }
}

// ─── Helpers ─────────────────────────────────────────────────

function reconstructDungeon(data: Record<string, unknown>): OctileDungeon {
    const dungeon = data as unknown as OctileDungeon
    const octCols = dungeon.octagonCols
    const octRows = dungeon.octagonRows

    // Rebuild full grid from sparse cell data for each layer
    for (const layer of dungeon.layers) {
        const sparseGrid = layer.grid as unknown as Array<{ r: number; c: number; kind: string; [key: string]: unknown }>
        layer.grid = deserializeGrid(sparseGrid, octCols, octRows)
    }

    return dungeon
}

function readFileAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result as string)
        reader.onerror = () => reject(new Error('Failed to read file'))
        reader.readAsText(file)
    })
}

/**
 * Remove characters that are unsafe for filenames.
 */
export function sanitizeFilename(name: string): string {
    return name
        .replace(/[\/\\:*?"<>|]/g, '')
        .replace(/\s+/g, '_')
        .trim() || 'dungeon'
}
